import {
  AbstractControl,
  AbstractControlOptions,
  AsyncValidatorFn,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
} from '@angular/forms';
import { getMetadata } from '@app/core/models/portal-metadata';
import { FormMappingModelPrototype } from '@app/utils/form-mapping/models/form-mapping-model';
import { getValueFromPath } from '@app/utils/get-value-from-path';
import { StringMap } from '@app/utils/string-map';
import { Subscription } from 'rxjs';
import { AutomaticallyDisposedSubscriptions } from '../../automatically-disposed-subscriptions';
import { deepcopy } from '../../deepmerge';
import { Logger } from '../../logger';
import { FormMappingOption } from '../models/form-mapping-option';

const SUBSCRIPTIONS = [];

/**
 * Creates helper functions to deal with the mapping between DataModel to ViewModel
 *
 * Example:
 *
 * Given an object of type A:
 * {
 *  prop1: {
 *    name: string,
 *    description: string
 *  },
 *  prop2: {
 *    email: string,
 *    info: string
 *  },
 *  prop3: {
 *    phone: string,
 *    fax: string
 *  }
 * }
 *
 * and the following form settings model (with mapping):
 *  - name (prop1.name)
 *  - description (prop1.description)
 *  - info (prop2.info)
 *  - phone (prop3.phone)
 *
 * The form mapping will create two static functions for a given type (look at the SettingsFormModel for an example):
 *  - from(o: A): FormSettings
 *  - merge(o: A, form: FormSettings): A
 *
 * These two functions provide the mapping between the DataModel and the ViewModel
 */
export function FormMapping(
  validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
  asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
) {
  return function <T extends FormMappingModelPrototype>(target: T) {
    target.prototype.formGroup = function (
      additionalConfig?: StringMap<object>,
      localValidatorOrOpts?: AbstractControlOptions
    ): UntypedFormGroup {
      const options = mergeOptions(validatorOrOpts, asyncValidator, localValidatorOrOpts);

      const formAttributes = getMetadata(target).formAttributes;
      const formGroup = new UntypedFormGroup({}, options);

      for (let attr of formAttributes) {
        const attribute = attr.attribute;
        let value = attr.initialValue !== undefined ? attr.initialValue : '';

        if (additionalConfig && additionalConfig[attribute]) {
          value = {
            ...additionalConfig[attribute],
            value,
          };
        }

        let control: AbstractControl;

        if (attr.isArray) {
          // We cannot create the necessary controls because at this point it is not guaranteed
          // that we know how many entries actually exist.
          control = new UntypedFormArray([], {
            asyncValidators: [].concat(attr.asyncValidators),
            validators: [].concat(attr.validators),
            updateOn: attr.updateOn,
          });
        } else if (attr.isGroup) {
          control = attr.formGroupFunction();
        } else {
          const formControl = new UntypedFormControl(value, {
            asyncValidators: attr.asyncValidators ? [].concat(attr.asyncValidators) : [],
            validators: attr.validators ? [].concat(attr.validators) : [],
            updateOn: attr.updateOn,
          });

          if (attr.normaliseInput !== undefined) {
            const subscription: Subscription = formControl.valueChanges.subscribe(function (value) {
              Logger.debug('valueChanges called');

              const newValue = attr.normaliseInput(value);

              formControl.setValue(newValue, {
                emitEvent: false,
                emitViewToModelChange: false,
              });
              formControl.updateValueAndValidity({ emitEvent: false });
            });

            AutomaticallyDisposedSubscriptions.push(subscription);
          }

          control = formControl;
        }

        formGroup.addControl(attr.attribute, control);
      }

      return formGroup;
    };

    target.prototype.from = function (o: StringMap<any>) {
      const formAttributes = getMetadata(target).formAttributes;
      const returnValue = {};

      for (let attr of formAttributes) {
        parseAttribute(returnValue, attr, o);
      }

      return returnValue;
    };

    target.prototype.merge = function (o: StringMap<any>, form: StringMap<any>) {
      const returnValue = o != null ? deepcopy(o) : {};

      // Get the list of form attributes
      const formAttributes: FormMappingOption[] = getMetadata(target).formAttributes;

      // Create the destination object
      for (let attr of formAttributes) {
        mergeAttribute(attr, returnValue, form);
      }

      // Merge the source object with the destination object
      return returnValue;
    };
  };
}

function createPathIfUnavailable(path: string | string[], o: StringMap<any>): object {
  if (typeof path === 'string') {
    path = path.split('.');
  }

  return path.reduce((a, b) => (a != null && a[b] !== undefined ? a[b] : (a[b] = {})), o);
}

/**
 * Merge the given attribute
 * @param attr The attribute to merge
 * @param initialValue The initial value
 * @param form The form from which the value will be given to the mergeFunction
 */
function mergeAttribute(attr: FormMappingOption, initialValue: any, form: StringMap<any>) {
  // Get the path array for the attribute ("a.b.c" => ['a', 'b', 'c']
  const path = attr.selector.split('.');

  // We don't want to create the property at this point so let's remove it (['a', 'b'])
  const attributeName = path.pop();

  // Create a object structure for the given path ({ a: { b: {} } })
  const value = path.reduce((a, b) => (a[b] = a[b] || {}), initialValue);

  let attributeValue = form[attr.attribute];

  // Can happen e.g. when using an empty option with [value]="undefined"
  if (attributeValue === 'null') {
    attributeValue = null;
  } else if (attributeValue === 'undefined') {
    attributeValue = undefined;
  }

  // Call the merge function that sets the property c in the previously created object ({ a: { b: { c: ... } } })
  attr.mergeFunction(value, attributeValue, attributeName, form);

  // Set default value if it was defined and the value is undefined after the merge function
  if (attr.defaultValue !== undefined && value[attributeName] === undefined) {
    value[attributeName] = attr.defaultValue;
  }
}

/**
 * Parse an attribute
 * @param returnValue Object to put the parsed value into
 * @param attr The form mapping attribute to parse
 * @param o The object to read from
 */
function parseAttribute(returnValue: StringMap<any>, attr: FormMappingOption, o: StringMap<any>) {
  let parsedValue = attr.parseFunction(getValueFromPath(attr.selector.split('.'), o), o);

  if (parsedValue === undefined && attr.defaultValue !== undefined) {
    parsedValue = attr.defaultValue;
  }

  returnValue[attr.attribute] = parsedValue;
}

/**
 * Clean up subscriptions that are used by normaliseInput functions
 */
export function performCleanup() {
  SUBSCRIPTIONS.forEach((subscription) => subscription.unsubscribe);

  Logger.debug('Unsubscribing', SUBSCRIPTIONS);

  SUBSCRIPTIONS.length = 0;
}

/**
 * Merge the given options (can be defined on the FormModel directly or when creating the formGroup in the component)
 * @param validatorOrOpts
 * @param asyncValidator
 * @param localValidatorOrOpts
 */
function mergeOptions(
  validatorOrOpts: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
  asyncValidator: AsyncValidatorFn | AsyncValidatorFn[] | null,
  localValidatorOrOpts: AbstractControlOptions | null
) {
  let options: {
    validators: ValidatorFn[];
    updateOn: 'change' | 'blur' | 'submit';
    asyncValidators: AsyncValidatorFn[];
  } = {
    asyncValidators: [],
    updateOn: 'change',
    validators: [],
  };

  if (typeof validatorOrOpts === 'function') {
    options.validators.push(validatorOrOpts);
  } else if (Array.isArray(validatorOrOpts)) {
    options.validators = [].concat(validatorOrOpts);
  } else if (typeof validatorOrOpts === 'object') {
    // AbstractControlOptions
    if (typeof validatorOrOpts.validators === 'function') {
      options.validators.push(validatorOrOpts.validators);
    } else if (Array.isArray(validatorOrOpts.validators)) {
      options.validators.push(...validatorOrOpts.validators);
    }

    if (typeof validatorOrOpts.asyncValidators === 'function') {
      options.asyncValidators.push(validatorOrOpts.asyncValidators);
    } else if (Array.isArray(validatorOrOpts.asyncValidators)) {
      options.asyncValidators.push(...validatorOrOpts.asyncValidators);
    }
  }

  if (typeof asyncValidator === 'function') {
    options.asyncValidators.push(asyncValidator);
  } else if (Array.isArray(asyncValidator)) {
    options.asyncValidators.push(...asyncValidator);
  }

  if (typeof localValidatorOrOpts === 'function') {
    options.validators.push(localValidatorOrOpts);
  } else if (Array.isArray(localValidatorOrOpts)) {
    options.validators.push(...localValidatorOrOpts);
  } else if (typeof localValidatorOrOpts === 'object') {
    if (typeof localValidatorOrOpts.validators === 'function') {
      options.validators.push(localValidatorOrOpts.validators);
    } else if (Array.isArray(localValidatorOrOpts.validators)) {
      options.validators.push(...localValidatorOrOpts.validators);
    }

    if (typeof localValidatorOrOpts.asyncValidators === 'function') {
      options.asyncValidators.push(localValidatorOrOpts.asyncValidators);
    } else {
      options.asyncValidators.push(...localValidatorOrOpts.asyncValidators);
    }

    if (localValidatorOrOpts.updateOn !== undefined) {
      if (options.updateOn !== 'change') {
        throw new Error('You cannot define updateOn on the FormMapping and when creating the FormGroup');
      }

      options.updateOn = localValidatorOrOpts.updateOn;
    }
  }

  return options;
}
